import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, Send, Trash2, Pencil, CheckCircle2, Clock, Mail, MailOpen,
  ArrowUpRight, AlertTriangle, X, Check, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { agentOnboardingAPI } from '@/lib/api';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : null);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');

const STATUS_META = {
  invited: { label: 'Invite sent', cls: 'bg-amber-100 text-amber-800', icon: Mail },
  pending: { label: 'Awaiting profile', cls: 'bg-amber-100 text-amber-800', icon: Mail },
  opened: { label: 'Link opened', cls: 'bg-sky-100 text-sky-800', icon: MailOpen },
  completed: { label: 'Profile completed', cls: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  expired: { label: 'Invite expired', cls: 'bg-rose-100 text-rose-700', icon: AlertTriangle },
  cancelled: { label: 'Invite cancelled', cls: 'bg-slate-200 text-slate-600', icon: AlertTriangle },
};

function MetaRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-800 text-right">{value}</span>
    </div>
  );
}

/**
 * Per-agent / per-invite onboarding detail panel. `target` is either
 * { kind: 'invite', linkId } or { kind: 'agent', npn }.
 */
export function OnboardingDetailDrawer({ open, onOpenChange, target, onChanged }) {
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    if (!target) return;
    setLoading(true);
    try {
      const { data } = target.kind === 'invite'
        ? await agentOnboardingAPI.inviteDetail(target.linkId)
        : await agentOnboardingAPI.agentDetail(target.npn);
      setDetail(data);
    } catch (e) {
      toast.error('Could not load onboarding detail');
    } finally {
      setLoading(false);
    }
  }, [target]);

  useEffect(() => {
    if (open) { setDetail(null); setEditing(false); setNewEmail(''); load(); }
  }, [open, load]);

  const isInvite = target?.kind === 'invite';
  const inv = isInvite ? detail : detail?.invite;
  const linkId = isInvite ? detail?.link_id : detail?.invite?.link_id;
  const status = isInvite ? detail?.status : (detail?.invite?.status || detail?.agent_status);
  const canResend = linkId && inv?.status !== 'completed';
  const canEditEmail = isInvite && detail?.status !== 'completed';

  const steps = detail?.steps || inv?.steps || [];
  const events = (isInvite ? detail?.step_events : inv?.step_events) || [];
  const furthest = (isInvite ? detail?.furthest_step : inv?.furthest_step) || 0;
  const eventByStep = {};
  events.forEach((e) => { eventByStep[e.step] = e; });

  const name = isInvite
    ? (`${detail?.first_name || ''} ${detail?.last_name || ''}`.trim() || detail?.email || 'Invited agent')
    : (detail?.agent_name || target?.npn);

  const meta = STATUS_META[status] || STATUS_META.pending;
  const StatusIcon = meta.icon;

  const resend = async () => {
    setBusy('resend');
    try {
      const { data } = await agentOnboardingAPI.resendLink(linkId);
      toast.success(data.email_sent ? `Invite re-sent to ${data.email}` : 'Fresh link ready');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resend the invite');
    } finally { setBusy(''); }
  };

  const saveEmail = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email.includes('@')) { toast.error('Enter a valid email'); return; }
    setBusy('email');
    try {
      const { data } = await agentOnboardingAPI.updateInviteEmail(linkId, email);
      toast.success(data.email_sent ? `Corrected — new link sent to ${data.email}` : `Email updated to ${data.email}`);
      setEditing(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update the email');
    } finally { setBusy(''); }
  };

  const doDelete = async () => {
    setBusy('delete');
    try {
      if (isInvite) await agentOnboardingAPI.deleteInvite(target.linkId);
      else await agentOnboardingAPI.deleteAgent(target.npn);
      toast.success(isInvite ? 'Invite deleted' : 'Onboarding profile deleted');
      setConfirmDelete(false);
      onOpenChange(false);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete');
    } finally { setBusy(''); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="onboarding-detail-drawer">
        <SheetHeader className="space-y-1">
          <SheetTitle className="font-heading flex items-center gap-2">{name}</SheetTitle>
          <SheetDescription>
            {isInvite ? 'Invited agent — onboarding in progress' : `Agent in onboarding${target?.npn ? ` · NPN ${target.npn}` : ''}`}
          </SheetDescription>
        </SheetHeader>

        {loading || !detail ? (
          <div className="flex h-48 items-center justify-center text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <div className="mt-4 space-y-5">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`text-[11px] ${meta.cls}`} data-testid="drawer-status-badge">
                <StatusIcon size={12} className="mr-1" /> {meta.label}
              </Badge>
              {inv?.from_prospect && (
                <Badge className="text-[11px] bg-indigo-100 text-indigo-700">From converted prospect</Badge>
              )}
            </div>

            {/* Step-by-step timeline */}
            <div data-testid="drawer-timeline">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Progress timeline</p>
              <ol className="relative space-y-3 pl-5">
                {steps.map((s) => {
                  const evt = eventByStep[s.step];
                  const reached = !!evt || furthest >= s.step || status === 'completed';
                  const at = evt?.at;
                  return (
                    <li key={s.step} className="relative" data-testid={`drawer-step-${s.step}`}>
                      <span
                        className={`absolute -left-5 top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full ring-2 ring-white ${reached ? 'bg-emerald-500' : 'bg-slate-200'}`}
                      >
                        {reached && <Check size={9} className="text-white" />}
                      </span>
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-sm ${reached ? 'text-slate-800 font-medium' : 'text-slate-400'}`}>
                          {s.label}
                        </span>
                        <span className="text-[10px] text-slate-400 shrink-0">
                          {at ? fmt(at) : (reached ? 'reached' : 'pending')}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>

            {/* What's still missing */}
            {isInvite && (detail.remaining_steps || []).length > 0 && (
              <div data-testid="drawer-remaining">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Still to complete</p>
                <div className="flex flex-wrap gap-1.5">
                  {detail.remaining_steps.map((s) => (
                    <span key={s.step} className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600 ring-1 ring-slate-200">
                      <Clock size={10} /> {s.label}
                    </span>
                  ))}
                </div>
                <p className="mt-1.5 text-[11px] text-slate-400">Nothing is saved until the agent submits the final step.</p>
              </div>
            )}
            {!isInvite && (detail.missing_fields || []).length > 0 && (
              <div data-testid="drawer-missing-fields">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Missing profile data</p>
                <div className="flex flex-wrap gap-1.5">
                  {detail.missing_fields.map((f) => (
                    <span key={f.key} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700 ring-1 ring-amber-200">
                      <AlertTriangle size={10} /> {f.label}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Meta */}
            <div className="rounded-lg border border-slate-100 px-3 py-1 divide-y divide-slate-50">
              {/* Email (editable for invites) */}
              <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
                <span className="text-slate-500">Email</span>
                {editing ? (
                  <div className="flex items-center gap-1.5 flex-1 max-w-[220px]">
                    <Input
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      className="h-8 text-xs"
                      placeholder="correct@email.com"
                      data-testid="drawer-email-input"
                    />
                    <Button size="icon" className="h-8 w-8 shrink-0 bg-emerald-600 hover:bg-emerald-700" onClick={saveEmail} disabled={busy === 'email'} data-testid="drawer-email-save">
                      {busy === 'email' ? <Loader2 size={13} className="animate-spin" /> : <Check size={14} />}
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => setEditing(false)} data-testid="drawer-email-cancel">
                      <X size={14} />
                    </Button>
                  </div>
                ) : (
                  <span className="font-medium text-slate-800 text-right flex items-center gap-1.5">
                    {inv?.email || detail.email || '—'}
                    {canEditEmail && (
                      <button
                        type="button"
                        className="text-slate-400 hover:text-slate-700"
                        onClick={() => { setNewEmail(inv?.email || detail.email || ''); setEditing(true); }}
                        data-testid="drawer-email-edit"
                        title="Correct the email"
                      >
                        <Pencil size={13} />
                      </button>
                    )}
                  </span>
                )}
              </div>
              {inv?.agency_name && <MetaRow label="Agency" value={inv.agency_name} />}
              {inv && <MetaRow label="Invite sent" value={fmtDate(inv.created_at)} />}
              {inv && inv.status !== 'completed' && <MetaRow label="Link opened" value={inv.opened_at ? fmt(inv.opened_at) : 'Not yet'} />}
              {inv?.last_step_at && <MetaRow label="Last activity" value={fmt(inv.last_step_at)} />}
              {inv?.status !== 'completed' && inv?.expires_at && <MetaRow label="Expires" value={fmtDate(inv.expires_at)} />}
              {inv?.resent_at && <MetaRow label="Last re-sent" value={`${fmtDate(inv.resent_at)}${inv.resent_by_email ? ` · ${inv.resent_by_email}` : ''}`} />}
              {inv?.created_by_email && <MetaRow label="Invited by" value={inv.created_by_email} />}
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2 pt-1">
              {canResend && (
                <Button variant="outline" onClick={resend} disabled={busy === 'resend'} data-testid="drawer-resend-btn">
                  {busy === 'resend' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />}
                  Resend invite
                </Button>
              )}
              {!isInvite && (
                <Button
                  variant="outline"
                  onClick={() => navigate(`/agents/${target.npn}?tab=onboarding`)}
                  data-testid="drawer-open-profile-btn"
                >
                  Open full profile <ArrowUpRight size={14} className="ml-1.5" />
                </Button>
              )}
              <Button
                variant="outline"
                className="text-rose-600 hover:text-rose-700 border-rose-200 hover:bg-rose-50"
                onClick={() => setConfirmDelete(true)}
                data-testid="drawer-delete-btn"
              >
                <Trash2 size={14} className="mr-1.5" />
                {isInvite ? 'Delete invite' : 'Delete onboarding profile'}
              </Button>
            </div>
          </div>
        )}

        <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <AlertDialogContent data-testid="drawer-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 font-heading">
                <AlertTriangle size={18} className="text-rose-600" />
                {isInvite ? 'Delete this invite?' : 'Delete this onboarding profile?'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {isInvite
                  ? 'The invitation link will be permanently removed. The agent will no longer be able to open it. This cannot be undone.'
                  : 'This permanently removes the agent record, their portal login and any onboarding links. This cannot be undone. Only agents still in onboarding can be deleted here.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="drawer-delete-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-rose-600 hover:bg-rose-700"
                onClick={(e) => { e.preventDefault(); doDelete(); }}
                disabled={busy === 'delete'}
                data-testid="drawer-delete-confirm"
              >
                {busy === 'delete' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Trash2 size={14} className="mr-1.5" />}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}

export default OnboardingDetailDrawer;
